import { secureStorage } from '../utils/secureStorage'

/**
 * خدمة تكوين API URL ديناميكياً
 * تسمح بتكوين عنوان API من:
 * - ملف تكوين خارجي (config.json)
 * - localStorage
 * - environment variable
 * - قيمة افتراضية
 */

const DEFAULT_API_URL = 'http://localhost:57688/api'
const ALLOWED_HOSTS = ['localhost', '127.0.0.1', 'smartaccount.com']

function isLoopbackHost(hostname: string): boolean {
  const host = hostname.replace(/^\[|\]$/g, '').toLowerCase()
  return host === 'localhost' || host === '127.0.0.1' || host === '::1'
}

/** Tailscale MagicDNS or CGNAT 100.64.0.0/10. */
export function isTailscaleHost(hostname: string): boolean {
  const host = hostname.replace(/^\[|\]$/g, '').toLowerCase()
  if (host.endsWith('.ts.net')) return true
  const ipv4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host)
  if (!ipv4) return false
  return Number(ipv4[1]) === 100 && Number(ipv4[2]) >= 64 && Number(ipv4[2]) <= 127
}

/** LAN, Tailscale CGNAT (100.64/10), MagicDNS, and local machine names. */
export function isPrivateOrTailscaleHost(hostname: string): boolean {
  const host = hostname.replace(/^\[|\]$/g, '').toLowerCase()
  if (isLoopbackHost(host)) return true
  if (host.endsWith('.ts.net') || host.endsWith('.local')) return true
  if (!host.includes('.')) return true

  const ipv4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host)
  if (!ipv4) return false
  const a = Number(ipv4[1])
  const b = Number(ipv4[2])
  if (a === 10 || a === 127) return true
  if (a === 192 && b === 168) return true
  if (a === 172 && b >= 16 && b <= 31) return true
  if (a === 100 && b >= 64 && b <= 127) return true
  return false
}

/**
 * Dev Kestrel HTTPS (57687) uses a local cert browsers reject.
 * Prefer HTTP on 57688 (same process / IIS backend binding).
 */
function normalizeApiBaseUrl(url: string): string {
  const trimmed = url.trim().replace(/\/$/, '')
  if (!trimmed) return DEFAULT_API_URL

  try {
    const parsed = new URL(trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`)
    if (
      (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') &&
      parsed.port === '57687'
    ) {
      return DEFAULT_API_URL
    }
  } catch {
    // fall through
  }

  return trimmed
}

function ensureApiSuffix(url: string): string {
  let normalized = url.trim()
  if (!normalized.endsWith('/api')) {
    normalized = normalized.endsWith('/') ? `${normalized}api` : `${normalized}/api`
  }
  return normalized
}

/**
 * HTTPS pages cannot call public http:// APIs (browser Mixed Content block).
 * Keep http for LAN/Tailscale; upgrade everything else when the UI is https.
 */
function upgradeToHttpsIfPageIsSecure(url: string): string {
  if (typeof window === 'undefined') return url
  if (window.location.protocol !== 'https:') return url
  try {
    const parsed = new URL(ensureApiSuffix(url))
    if (parsed.protocol !== 'http:') return url
    if (isPrivateOrTailscaleHost(parsed.hostname)) return url
    parsed.protocol = 'https:'
    return parsed.toString().replace(/\/$/, '')
  } catch {
    return url
  }
}

function isValidApiUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:') return true
    if (parsed.protocol !== 'http:') return false
    return isPrivateOrTailscaleHost(parsed.hostname) || ALLOWED_HOSTS.some(host => parsed.hostname === host)
  } catch {
    return false
  }
}

function originPort(protocol: string, port: string): string {
  if (port) return port
  return protocol === 'https:' ? '443' : '80'
}

/** True when the URL is the UI site itself (e.g. :8080), not the API (:8082 / :57688). */
function isSameOriginAsPage(url: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    const parsed = new URL(url.startsWith('http') ? url : `http://${url}`)
    const page = window.location
    return (
      parsed.hostname.replace(/^\[|\]$/g, '').toLowerCase() ===
        page.hostname.replace(/^\[|\]$/g, '').toLowerCase() &&
      originPort(parsed.protocol, parsed.port) === originPort(page.protocol, page.port)
    )
  } catch {
    return false
  }
}

/** Page opened from another PC (LAN / Tailscale), not localhost on server X. */
export function isRemoteBrowserPage(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return !!host && !isLoopbackHost(host)
}

/** Page is on a Tailscale IP or MagicDNS name (not LAN 192.168/10.x). */
export function isTailscaleBrowserPage(): boolean {
  if (typeof window === 'undefined') return false
  return isTailscaleHost(window.location.hostname)
}

export const LAN_HEARTBEAT_MS = 60_000
/** Sparse enough that several browsers can share one Tailscale/DERP tunnel. */
export const TAILSCALE_HEARTBEAT_MS = 180_000

/** Presence / poll cadence: localhost stays snappy, Tailscale stays sparse. */
export function getRemoteHeartbeatIntervalMs(
  localMs: number,
  lanMs: number = LAN_HEARTBEAT_MS,
  tailscaleMs: number = TAILSCALE_HEARTBEAT_MS,
): number {
  if (typeof window === 'undefined' || !isRemoteBrowserPage()) return localMs
  if (isTailscaleBrowserPage()) return tailscaleMs
  return lanMs
}

/** Spread heartbeats so N devices on the same MagicDNS host do not fire together. */
export function withHeartbeatJitter(ms: number): number {
  const spread = 0.4
  return Math.max(5_000, Math.round(ms * (1 - spread / 2 + Math.random() * spread)))
}

/** Session-stable delay so several PCs do not all handshake SignalR at once. */
export function getDeviceStartStaggerMs(): number {
  if (typeof window === 'undefined') return Math.floor(Math.random() * 2000)
  try {
    const key = 'sa-ts-stagger-ms'
    const existing = Number(sessionStorage.getItem(key) || 0)
    if (existing > 0) return existing
    const n = 250 + Math.floor(Math.random() * 2500)
    sessionStorage.setItem(key, String(n))
    return n
  } catch {
    return 250 + Math.floor(Math.random() * 2500)
  }
}

export async function delayForTailscaleStagger(extraMs = 0): Promise<void> {
  const ms = (isTailscaleBrowserPage() ? getDeviceStartStaggerMs() : 0) + extraMs
  if (ms <= 0) return
  await new Promise<void>((resolve) => window.setTimeout(resolve, ms))
}

export function getSignalRReconnectDelays(): number[] {
  if (isTailscaleBrowserPage()) return [2_000, 8_000, 20_000, 45_000, 90_000]
  if (isRemoteBrowserPage()) return [1_000, 4_000, 10_000, 20_000]
  return [0, 1_000, 3_000, 8_000, 15_000]
}

export function isBackgroundTrafficPaused(): boolean {
  return typeof document !== 'undefined' && document.hidden
}

type RuntimeConfig = {
  apiBaseUrl?: string
  /** Default true: https://*.ts.net uses origin/api (Tailscale Serve). */
  useSameOriginOnTailscale?: boolean
  /** Dangerous: treat http://100.x:8080 as the API. Needs IIS ARR. */
  forceSameOriginHttp?: boolean
}

let runtimeConfig: RuntimeConfig | null = null

function sameOriginApiUrl(): string {
  return `${window.location.origin}/api`
}

/**
 * Tailscale Serve (https://*.ts.net or :443/:80 MagicDNS) reverse-proxies /api.
 * Bare http://100.x:8080 has no proxy — never treat that as the API (IIS 405).
 */
export function shouldUseSameOriginApi(): boolean {
  if (typeof window === 'undefined') return false
  if (!isRemoteBrowserPage()) return false
  if (runtimeConfig?.forceSameOriginHttp === true) return true
  if (runtimeConfig?.useSameOriginOnTailscale === false) return false

  if (window.location.protocol === 'https:') return true

  const host = window.location.hostname.replace(/^\[|\]$/g, '').toLowerCase()
  const port = window.location.port
  const servePort = !port || port === '80' || port === '443'
  return host.endsWith('.ts.net') && servePort
}

/**
 * On a remote PC, keep the API port from config.json (57688 / 8082).
 * Only swap the hostname. Never call the UI origin (:8080 / :800): IIS
 * static files return 405 Method Not Allowed on POST /api/...
 */
function rewriteLocalApiHostForRemotePage(url: string): string {
  if (typeof window === 'undefined') return url
  if (shouldUseSameOriginApi()) return sameOriginApiUrl()
  try {
    const pageHost = window.location.hostname
    if (!pageHost || isLoopbackHost(pageHost)) return url

    const parsed = new URL(ensureApiSuffix(url))
    const apiHostIsLocalOrPrivate =
      isLoopbackHost(parsed.hostname) || isPrivateOrTailscaleHost(parsed.hostname)
    if (!apiHostIsLocalOrPrivate && !isSameOriginAsPage(parsed.toString())) return url

    parsed.hostname = pageHost
    parsed.protocol = window.location.protocol === 'https:' ? 'https:' : 'http:'

    // If config/localStorage pointed at the UI site itself, restore Kestrel port.
    if (isSameOriginAsPage(parsed.toString())) {
      parsed.port = '57688'
    }

    return parsed.toString().replace(/\/$/, '')
  } catch {
    return url
  }
}

export async function loadRuntimeConfig(): Promise<void> {
  try {
    const response = await fetch('/config.json', {
      cache: 'no-store',
      signal: AbortSignal.timeout(2000),
    })
    if (!response.ok) return
    const json = (await response.json()) as RuntimeConfig
    runtimeConfig = json || null
  } catch {
    // تجاهل أي أخطاء في قراءة ملف التكوين الخارجي
  }
}

/**
 * الحصول على API Base URL
 * الأولوية:
 * 1. localStorage (apiBaseUrl) - للتكوين اليدوي
 * 2. ملف config.json الخارجي (apiBaseUrl)
 * 3. Environment variable (VITE_API_BASE_URL)
 * 4. القيمة الافتراضية
 */
export function getApiBaseUrl(): string {
  if (shouldUseSameOriginApi()) {
    return sameOriginApiUrl()
  }

  // localStorage wins only when it is a real API URL, not the UI origin (:8080).
  const savedUrl = secureStorage.getItem('apiBaseUrl')
  if (savedUrl && savedUrl.trim()) {
    const url = ensureApiSuffix(normalizeApiBaseUrl(savedUrl))

    // Persist migration away from broken localhost:57687 HTTPS
    if (url !== ensureApiSuffix(savedUrl.trim()) && isValidApiUrl(url)) {
      secureStorage.setItem('apiBaseUrl', url.replace(/\/api$/, ''))
    }

    if (isValidApiUrl(url) && isSameOriginAsPage(url)) {
      // UI origin was saved by mistake (405 on POST). Prefer config.json port.
    } else if (isValidApiUrl(url)) {
      return upgradeToHttpsIfPageIsSecure(rewriteLocalApiHostForRemotePage(url))
    }
    if (!isValidApiUrl(url)) {
      console.warn('Blocked potentially malicious API URL:', url)
    }
  }

  // config.json on the server (e.g. http://localhost:8082/api)
  if (runtimeConfig?.apiBaseUrl && runtimeConfig.apiBaseUrl.trim()) {
    return upgradeToHttpsIfPageIsSecure(
      rewriteLocalApiHostForRemotePage(
        ensureApiSuffix(normalizeApiBaseUrl(runtimeConfig.apiBaseUrl)),
      ),
    )
  }

  // محاولة الحصول من environment variable
  const envUrl = (import.meta as any).env?.VITE_API_BASE_URL
  if (envUrl && envUrl.trim()) {
    return upgradeToHttpsIfPageIsSecure(
      rewriteLocalApiHostForRemotePage(ensureApiSuffix(normalizeApiBaseUrl(envUrl))),
    )
  }

  // القيمة الافتراضية
  return upgradeToHttpsIfPageIsSecure(rewriteLocalApiHostForRemotePage(DEFAULT_API_URL))
}

/**
 * حفظ API Base URL في localStorage
 */
export function setApiBaseUrl(url: string): void {
  if (!url || !url.trim()) {
    secureStorage.removeItem('apiBaseUrl')
    return
  }

  // تنظيف الرابط
  let cleanUrl = url.trim()
  
  // إزالة /api من النهاية إذا كان موجوداً (سنضيفه تلقائياً)
  if (cleanUrl.endsWith('/api')) {
    cleanUrl = cleanUrl.slice(0, -4)
  }
  if (cleanUrl.endsWith('/')) {
    cleanUrl = cleanUrl.slice(0, -1)
  }

  // LAN / Tailscale IPs use HTTP; public hosts default to HTTPS
  if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
    const hostOnly = cleanUrl.split('/')[0].split(':')[0]
    const useHttp = isPrivateOrTailscaleHost(hostOnly)
    cleanUrl = (useHttp ? 'http://' : 'https://') + cleanUrl
  }

  cleanUrl = normalizeApiBaseUrl(cleanUrl).replace(/\/api$/, '')

  // التحقق النهائي من الصحة قبل الحفظ
  if (isValidApiUrl(cleanUrl)) {
    secureStorage.setItem('apiBaseUrl', cleanUrl)
    // إعادة تحميل الصفحة لتطبيق التغييرات
    window.location.reload()
  } else {
    throw new Error('Invalid or disallowed API URL')
  }
}

/**
 * الحصول على API Base URL الحالي (للعرض فقط)
 */
export function getCurrentApiUrl(): string {
  return getApiBaseUrl()
}

/**
 * اختبار الاتصال بـ API URL
 */
export async function testApiConnection(url: string): Promise<{ success: boolean; message: string }> {
  try {
    let testUrl = url.trim()
    if (!testUrl.endsWith('/api')) {
      testUrl = testUrl.endsWith('/') ? testUrl + 'api' : testUrl + '/api'
    }

    // إضافة http:// إذا لم يكن موجوداً
    if (!testUrl.startsWith('http://') && !testUrl.startsWith('https://')) {
      const hostOnly = testUrl.split('/')[0].split(':')[0]
      const useHttp = isPrivateOrTailscaleHost(hostOnly)
      testUrl = (useHttp ? 'http://' : 'https://') + testUrl
    }
    
    const parsedTest = new URL(testUrl)
    const remotePrivate = isPrivateOrTailscaleHost(parsedTest.hostname) && !isLoopbackHost(parsedTest.hostname)
    const response = await fetch(`${testUrl}/deviceregistration/check/test`, { 
      method: 'GET',
      mode: 'cors',
      signal: AbortSignal.timeout(remotePrivate ? 20000 : 5000)
    })
    
    if (response.ok) {
      return { success: true, message: 'تم الاتصال بنجاح' }
    } else {
      return { success: false, message: `فشل الاتصال: ${response.status} ${response.statusText}` }
    }
  } catch (error: any) {
    console.error('API Test Error:', error)
    return { success: false, message: `خطأ في الاتصال: ${error.message || 'لا يمكن الوصول للسيرفر'}` }
  }
}

/**
 * إزالة الإعدادات المخصصة والعودة للقيم الافتراضية
 */
export function resetApiConfig(): void {
  secureStorage.removeItem('apiBaseUrl')
  window.location.reload()
}

